import { useState } from "react";
import { CheckCircle, Scroll, Warning } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TO_LIST = [
  "The Hon'ble Chief Minister of Karnataka",
  "Chief Secretary, Govt of Karnataka",
  "Bruhat Bengaluru Mahanagara Palike (BBMP)",
  "Bengaluru Development Authority (BDA)",
  "Bengaluru Water Supply and Sewerage Board (BWSSB)",
  "Karnataka State Pollution Control Board",
  "Karnataka Tank Conservation and Development Authority",
  "National Green Tribunal, South Zone",
];

const DEMANDS = [
  "Fully implement all pending orders of the National Green Tribunal, High Court and Supreme Court relating to Bellandur Lake.",
  "Immediately halt construction of any new roads, bund roads or hard infrastructure within Bellandur Lake, its wetlands, floodplains and connected rajakaluves.",
  "Remove illegal obstructions and restore the natural hydrological connectivity and water flow into, within and out of the lake.",
  "Prevent untreated and partially treated sewage from entering Bellandur Lake through complete sewage interception and treatment.",
  "Restore wetlands, marshlands and biodiversity corridors that naturally absorb floods and recharge groundwater.",
  "Establish transparent public monitoring of restoration works with regular disclosure of progress and environmental data.",
  "Create an ecologically friendly non-permanent mud walking track around the lake periphery for citizen usage and health.",
];

export default function PetitionPage() {
  const [form, setForm] = useState({ name: "", email: "", address: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const fontStack = "'IBM Plex Sans', 'Noto Sans Kannada', sans-serif";
  const headingStack = "'Cormorant Garamond', 'Noto Serif Kannada', Georgia, serif";

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address.";
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setServerError("");
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/petition-signatures`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          address: form.address.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Server error");
      }
      setSubmitted(true);
    } catch (err) {
      setServerError(err.message === "Not Found"
        ? "Petition submission is not enabled on the server yet. Please try again after the backend is updated."
        : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="petition-page">

      {/* Page Header */}
      <section
        className="py-20 md:py-28 border-b"
        style={{ backgroundColor: '#E8EDE9', borderColor: '#D3DCD6' }}
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p
            className="text-xs uppercase tracking-[0.2em] font-medium mb-4"
            style={{ color: '#B85C38', fontFamily: fontStack }}
          >
            Citizens&apos; Action
          </p>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium mb-4"
            style={{ color: '#1C211F', fontFamily: headingStack }}
          >
            Citizens&apos; Petition
          </h1>
          <p
            className="text-xl font-medium"
            style={{ color: '#1A5C37', fontFamily: headingStack }}
          >
            Save Bellandur Lake
          </p>
        </div>
      </section>

      {/* Petition Body */}
      <section
        className="py-16 md:py-24"
        style={{ backgroundColor: '#F7F5F0' }}
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12">

          {/* "To" Block */}
          <div
            className="rounded-lg border p-6 md:p-8 mb-10"
            style={{
              backgroundColor: '#FFFFFF',
              borderColor: 'rgba(26, 92, 55, 0.15)',
              borderLeft: '4px solid #1A5C37',
            }}
          >
            <p
              className="text-xs uppercase tracking-[0.18em] font-semibold mb-4"
              style={{ color: '#1A5C37', fontFamily: fontStack }}
            >
              To,
            </p>
            <ul className="space-y-1">
              {TO_LIST.map((item, i) => (
                <li
                  key={i}
                  className="text-sm md:text-base leading-relaxed"
                  style={{ color: '#1C211F', fontFamily: fontStack }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Petition Body Text */}
          <div className="space-y-6 mb-10">
            <p
              className="text-base md:text-lg leading-relaxed"
              style={{ color: '#2C3530', fontFamily: fontStack }}
            >
              {"Bellandur Lake is a critical ecological asset, Bengaluru's largest lake and the heart of an interconnected network of lakes, wetlands and rajakaluves that protects the city from flooding, recharges groundwater, sustains biodiversity, and strengthens climate resilience."}
            </p>
            <p
              className="text-base md:text-lg leading-relaxed"
              style={{ color: '#2C3530', fontFamily: fontStack }}
            >
              For years, the National Green Tribunal, the High Court and the Supreme Court have issued directions to restore Bellandur Lake. Yet many of these directions remain only partially implemented. We urge the Government of Karnataka to act with urgency by implementing these judicial directions in letter and spirit and before irreversible ecological damage occurs. Clean water, ecological security and protection from flooding are fundamental rights.
            </p>
            <p
              className="text-base md:text-lg leading-relaxed font-medium"
              style={{ color: '#1C211F', fontFamily: fontStack }}
            >
              We therefore call upon the Government of Karnataka and all concerned authorities to:
            </p>
          </div>

          {/* Demands */}
          <ol className="space-y-4 mb-12">
            {DEMANDS.map((demand, i) => (
              <li
                key={i}
                className="flex gap-4 p-4 md:p-5 rounded-lg border"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderColor: 'rgba(26, 92, 55, 0.12)',
                }}
              >
                <span
                  className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold mt-0.5"
                  style={{
                    backgroundColor: 'rgba(26, 92, 55, 0.1)',
                    color: '#1A5C37',
                    fontFamily: fontStack,
                  }}
                >
                  {i + 1}
                </span>
                <p
                  className="text-sm md:text-base leading-relaxed"
                  style={{ color: '#2C3530', fontFamily: fontStack }}
                >
                  {demand}
                </p>
              </li>
            ))}
          </ol>

          {/* Closing Statement */}
          <div
            className="rounded-lg border p-6 md:p-8 mb-16"
            style={{
              backgroundColor: 'rgba(26, 92, 55, 0.05)',
              borderColor: 'rgba(26, 92, 55, 0.2)',
            }}
          >
            <p
              className="text-base md:text-lg leading-relaxed text-center font-medium"
              style={{ color: '#1A5C37', fontFamily: fontStack, fontStyle: 'italic' }}
            >
              {"Bellandur Lake belongs to every citizen of Bengaluru. Its protection is essential for the city's sustainable future."}
            </p>
          </div>

          {/* Divider */}
          <div
            className="border-t mb-16"
            style={{ borderColor: 'rgba(26, 92, 55, 0.15)' }}
          />

          {/* Signature Form */}
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-10">
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4"
                style={{ backgroundColor: 'rgba(184, 92, 56, 0.1)' }}
              >
                <Scroll size={24} weight="duotone" style={{ color: '#B85C38' }} />
              </div>
              <h2
                className="font-heading text-2xl sm:text-3xl tracking-tight font-medium mb-2"
                style={{ color: '#1C211F', fontFamily: headingStack }}
              >
                Sign the Petition
              </h2>
              <p
                className="text-sm leading-relaxed"
                style={{ color: '#4A5450', fontFamily: fontStack }}
              >
                Add your name to this petition and stand with Bellandur Lake.
              </p>
            </div>

            {submitted ? (
              <div
                className="text-center py-12 px-6 rounded-lg border"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderColor: 'rgba(26, 92, 55, 0.15)',
                }}
              >
                <CheckCircle
                  size={48}
                  weight="duotone"
                  className="mx-auto mb-4"
                  style={{ color: '#1A5C37' }}
                />
                <h3
                  className="font-heading text-xl font-medium mb-2"
                  style={{ color: '#1C211F', fontFamily: headingStack }}
                >
                  Thank you for signing!
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: '#4A5450', fontFamily: fontStack }}
                >
                  Your signature has been recorded. Together, we can protect Bellandur Lake.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                noValidate
                className="relative rounded-lg border p-6 md:p-8 space-y-6"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderColor: 'rgba(26, 92, 55, 0.15)',
                }}
              >
                {submitting && (
                  <div
                    className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg"
                    style={{ backgroundColor: 'rgba(255,255,255,0.88)' }}
                  >
                    <div className="w-8 h-8 border-2 rounded-full animate-spin mb-3" style={{ borderColor: '#1A5C37', borderTopColor: 'transparent' }} />
                    <p className="text-sm font-medium" style={{ color: '#1C211F', fontFamily: fontStack }}>Submitting...</p>
                  </div>
                )}

                {/* Name */}
                <div>
                  <label
                    className="block text-sm font-medium mb-1.5"
                    style={{ color: '#1C211F', fontFamily: fontStack }}
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border text-sm outline-none transition-colors"
                    style={{
                      borderColor: errors.name ? '#B85C38' : 'rgba(26, 92, 55, 0.2)',
                      backgroundColor: '#F7F5F0',
                      color: '#1C211F',
                      fontFamily: fontStack,
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#1A5C37')}
                    onBlur={(e) => (e.target.style.borderColor = errors.name ? '#B85C38' : 'rgba(26, 92, 55, 0.2)')}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs flex items-center gap-1" style={{ color: '#B85C38', fontFamily: fontStack }}>
                      <Warning size={12} /> {errors.name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label
                    className="block text-sm font-medium mb-1.5"
                    style={{ color: '#1C211F', fontFamily: fontStack }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border text-sm outline-none transition-colors"
                    style={{
                      borderColor: errors.email ? '#B85C38' : 'rgba(26, 92, 55, 0.2)',
                      backgroundColor: '#F7F5F0',
                      color: '#1C211F',
                      fontFamily: fontStack,
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#1A5C37')}
                    onBlur={(e) => (e.target.style.borderColor = errors.email ? '#B85C38' : 'rgba(26, 92, 55, 0.2)')}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs flex items-center gap-1" style={{ color: '#B85C38', fontFamily: fontStack }}>
                      <Warning size={12} /> {errors.email}
                    </p>
                  )}
                </div>

                {/* Address */}
                <div>
                  <label
                    className="block text-sm font-medium mb-1.5"
                    style={{ color: '#1C211F', fontFamily: fontStack }}
                  >
                    Address <span className="font-normal" style={{ color: '#7A8A84' }}>(optional)</span>
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Your address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border text-sm outline-none transition-colors"
                    style={{
                      borderColor: 'rgba(26, 92, 55, 0.2)',
                      backgroundColor: '#F7F5F0',
                      color: '#1C211F',
                      fontFamily: fontStack,
                      resize: 'vertical',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#1A5C37')}
                    onBlur={(e) => (e.target.style.borderColor = 'rgba(26, 92, 55, 0.2)')}
                  />
                </div>

                {serverError && (
                  <p
                    className="text-sm flex items-center gap-2 p-3 rounded-lg"
                    style={{
                      color: '#B85C38',
                      backgroundColor: 'rgba(184, 92, 56, 0.06)',
                      fontFamily: fontStack,
                    }}
                  >
                    <Warning size={16} /> {serverError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 rounded-full text-sm font-medium transition-colors duration-200"
                  style={{
                    backgroundColor: submitting ? '#4A5450' : '#1A5C37',
                    color: '#F7F5F0',
                    fontFamily: fontStack,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={(e) => { if (!submitting) e.target.style.backgroundColor = '#154D2E'; }}
                  onMouseLeave={(e) => { if (!submitting) e.target.style.backgroundColor = '#1A5C37'; }}
                >
                  {submitting ? "Submitting..." : "Sign the Petition"}
                </button>
              </form>
            )}
          </div>

        </div>
      </section>
    </div>
  );
}
